import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { CreateOperatorStatusError, UpdateOperatorStatusError } from '../../errors';
import { OperatorStatus, OperatorStatusType, User } from '../../models';

import { AuthenticationService } from '../authentication.service';
import { HttpRequestService } from '../http';
import { OperatorStatusesService } from '../operator-statuses.service';
import { UsersService } from '../users.service';

import { FirestoreTimestamp } from './firestore-authentication.service';
import { FirestoreService } from './firestore.service';

export interface OperatorStatusDTO {
    backgroundColor: string; 
    color: string; 
    createdAt: FirestoreTimestamp;
    createdBy: string;
    id: string;
    name: string;
    type: string;
}

@Injectable({
    providedIn: 'root',
})
export class FirestoreOperatorStatusesService implements OperatorStatusesService {
    private _operatorStatusesSubject: Subject<OperatorStatus[]>;

    private _operatorStatuses: OperatorStatus[];

    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService,
        private httpService: HttpRequestService,
        private translateService: TranslateService,
        private usersService: UsersService
    ) {
        this._operatorStatusesSubject = new ReplaySubject<OperatorStatus[]>(1);

        this.initFirestoreCollectionObservable();
    }

    operatorStatuses(): Observable<OperatorStatus[]> {
        return this._operatorStatusesSubject.asObservable();
    }

    async operatorStatus(id: string): Promise<OperatorStatus> {
        const findOperatorStatus = (operatorStatus: OperatorStatus[]) => operatorStatus.find((o) => o.id === id);

        return new Promise<OperatorStatus>((resolve) => {
            if (this._operatorStatuses) {
                // operator statuses are already synced with firestore
                resolve(findOperatorStatus(this._operatorStatuses));
            } else {
                // operator statuses are not yet resolver, wait until the _operatorStatusesSubject has any data to resolve it
                this._operatorStatusesSubject.pipe(take(1)).subscribe((operatorStatuses) => resolve(findOperatorStatus(operatorStatuses)));
            }
        });
    }

    operatorStatusColors(labels: string[], operatorStatuses: OperatorStatus[]): string[] {
        const operatorStatusColors = [];

        if (labels?.length > 0 && operatorStatuses?.length > 0) {
            labels.forEach((label: string) => {
                const correspondingOperatorStatus = operatorStatuses.find((operatorStatus: OperatorStatus) => operatorStatus.id === label);
    
                if (correspondingOperatorStatus) {
                    operatorStatusColors.push(correspondingOperatorStatus.color);
                }
            });
        }

        return operatorStatusColors;
    }

    async createOperatorStatus(backgroundColor: string, color: string, name: string): Promise<OperatorStatus> {
        const tenantId = this.authenticationService.user.tenantId;
        const createOperatorStatus = this.firestoreService.cloudFunction('OperatorStatuses_CreateOperatorStatus');
        const result = await createOperatorStatus({ backgroundColor, color, name, tenantId });

        if (result && result.data && result.data.code) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new CreateOperatorStatusError('PERMISSION_DENIED', 'The authenticated user don\'t have permissions to perform this action.');
                case 'invalid-argument':
                    throw new CreateOperatorStatusError('MISSING_ARGUMENT', 'The property \'backgroundColor\', \'color\', \'name\' or \'tenantId\' is missing.');
                case 'already-exists':
                    throw new CreateOperatorStatusError('NAME_ALREADY_EXISTS', 'There are already another \'OperatorStatus\' with this name.');
                default:
                    throw new CreateOperatorStatusError('UNKNOWN', 'An unknown error occurred.');
            }
        }

        return result.data;
    }

    async updateOperatorStatus(operatorStatusId: string, data: { 
        backgroundColor: string;
        color: string;
        name: string; 
    }): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const updateOperatorStatus = this.firestoreService.cloudFunction('OperatorStatuses_UpdateOperatorStatus');
        const result = await updateOperatorStatus({ operatorStatusId, tenantId, ...data });

        if (result && result.data) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new UpdateOperatorStatusError('PERMISSION_DENIED', 'The authenticated user don\'t have permissions to perform this action.');
                case 'invalid-argument':
                    throw new UpdateOperatorStatusError('MISSING_ARGUMENT', 'The property \'backgroundColor\', \'color\', \'name\', or \'tenantId\' is missing.');
                case 'already-exists':
                    throw new UpdateOperatorStatusError('NAME_ALREADY_EXISTS', 'There are already another \'OperatorStatus\' with this name.');
                default:
                    throw new UpdateOperatorStatusError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    private initFirestoreCollectionObservable(): void {
        combineLatest([
            this.firestoreService.tenantDependentCollection<OperatorStatusDTO>(this.authenticationService.user.tenantId, 'operatorStatuses')
                .snapshotChanges(),
            this.usersService.users(),
        ]).subscribe((result) => {
            const operatorStatusesDTO = result[0].map((os) => {
                const operatorStatus = os.payload.doc.data();
                operatorStatus.id = os.payload.doc.id;
                return operatorStatus;
            });

            const operatorStatuses = operatorStatusesDTO.map((operatorStatusDTO) => 
                this.operatorStatusMapper(operatorStatusDTO, result[1])
            );

            this._operatorStatuses = operatorStatuses;
            this.updateOperatorStatuses(operatorStatuses);
        });
    }

    private instanceTagType(operatorStatusType: string): boolean {
        return ['default', 'custom'].indexOf(operatorStatusType) >= 0;
    }

    private operatorStatusMapper(operatorStatusDTO: OperatorStatusDTO, users: User[]): OperatorStatus {
        return {
            backgroundColor: operatorStatusDTO.backgroundColor, 
            color: operatorStatusDTO.color, 
            createdAt: operatorStatusDTO.createdAt && operatorStatusDTO.createdAt.toDate(),
            createdBy: users.find((user) => user.id === operatorStatusDTO.createdBy),
            id: operatorStatusDTO.id,
            name: operatorStatusDTO.name,
            type: operatorStatusDTO.type && this.instanceTagType(operatorStatusDTO.type) ? (operatorStatusDTO.type as OperatorStatusType) : 'default',
        };
    }

    private updateOperatorStatuses(operatorStatuses: OperatorStatus[]): void {
        this._operatorStatusesSubject.next(operatorStatuses);
    }
}
